import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3107";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Réponse GET /U3107/initLU — titre UI = i18n `title`. */
export interface U3107InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode?: string;
}

/** Ligne retournée par POST /U3107/search. */
export interface U3107EquipmentSearchRow {
  id: string;
  code?: string;
  description?: string | null;
  billingUnit?: string;
  amount?: number;
  active?: number;
  majorFactor?: number;
  version: number;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

/** Détail GET /U3107/:id (result). */
export interface U3107Equipment {
  id: string;
  code: string;
  description: string | null;
  billingUnit: string;
  amount: number;
  active: number;
  majorFactor: number;
  version: number;
}

export type U3107EquipmentFormRow = Omit<U3107Equipment, "description" | "majorFactor" | "amount"> & {
  description: string;
  billingUnit: string;
  amount: number | null;
  majorFactor?: number;
};

export interface U3107EquipmentCreateBody {
  code: string;
  description: string;
  billingUnit: string;
  amount: number;
  majorFactor: number;
  active?: number;
}

export interface U3107EquipmentUpdateBody {
  id: string;
  code: string;
  description: string;
  billingUnit: string;
  amount: number;
  majorFactor: number;
  version: number;
  active?: number;
}

export interface U3107EquipmentDeleteBody {
  id: string;
  version: number;
}

export function createEmptyU3107FormRow(): U3107EquipmentFormRow {
  return {
    id: "",
    code: "",
    description: "",
    billingUnit: "",
    amount: null,
    majorFactor: undefined,
    active: 1,
    version: 0,
  };
}

export function normalizeU3107EquipmentFromApi(row: U3107Equipment): U3107Equipment {
  return {
    ...row,
    description: row.description ?? null,
    majorFactor: row.majorFactor ?? 0,
    active: row.active ?? 1,
    amount: row.amount ?? 0,
  };
}

export function formRowToApiEquipment(row: U3107EquipmentFormRow): U3107Equipment {
  const majorFactor =
    typeof row.majorFactor === "number" && !Number.isNaN(row.majorFactor) ? row.majorFactor : 0;
  const amount =
    typeof row.amount === "number" && !Number.isNaN(row.amount) ? row.amount : 0;
  const active = typeof row.active === "number" && !Number.isNaN(row.active) ? row.active : 1;
  return {
    ...row,
    description: row.description.trim() === "" ? null : row.description.trim(),
    billingUnit: row.billingUnit.trim(),
    amount,
    majorFactor,
    active,
  };
}

export const U3107_ERROR_CODE_DUPLICATE = "BU310701";

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3107EquipmentCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3107EquipmentUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3107EquipmentDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function buildCreateBody(data: U3107EquipmentFormRow): U3107EquipmentCreateBody {
  const m = formRowToApiEquipment(data);
  const body: U3107EquipmentCreateBody = {
    code: m.code.trim(),
    description: m.description ?? "",
    billingUnit: m.billingUnit,
    amount: m.amount,
    majorFactor: m.majorFactor,
  };
  if (m.active !== 1) {
    body.active = m.active;
  }
  return body;
}

export function buildUpdateBody(data: U3107Equipment): U3107EquipmentUpdateBody {
  return {
    id: data.id,
    code: data.code.trim(),
    description: data.description ?? "",
    billingUnit: data.billingUnit.trim(),
    amount: data.amount,
    majorFactor: data.majorFactor ?? 0,
    version: data.version,
    active: data.active ?? 1,
  };
}

export function buildDeleteBody(data: U3107Equipment): U3107EquipmentDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3107_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors["code"] = t("errCodeDuplicate", { ns: nameSpace });
      break;
    default:
      break;
  }
}
